import {
  FileText,
  Gavel,
  Calculator,
  ShoppingCart,
  Ship,
  Microscope,
  Radar,
  PackageCheck,
} from "lucide-react";
import { CIF } from "@/constants/testIds";

const NODES = [
  { key: "rfq", label: "RFQ™", icon: FileText, sub: "Request quotes" },
  { key: "commodity-bid", label: "CommodityBid™", icon: Gavel, sub: "Bidding & award" },
  { key: "estimated-cif", label: "Estimated CIF™", icon: Calculator, sub: "Landed cost · Pre-PO", current: true },
  { key: "purchase-order", label: "Purchase Order", icon: ShoppingCart, sub: "Approve & commit" },
  { key: "freight-iq", label: "FreightIQ™", icon: Ship, sub: "Book ocean freight" },
  { key: "inspection", label: "Inspection™", icon: Microscope, sub: "Pre-shipment QA" },
  { key: "live-tracking", label: "Live Shipment Tracking™", icon: Radar, sub: "ETA & milestones" },
  { key: "delivered", label: "Delivered", icon: PackageCheck, sub: "Closeout" },
];

export const WorkspaceIntegration = () => {
  return (
    <section
      id="workspace"
      data-testid={CIF.workspace}
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> Workspace Integration
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-medium tracking-[-0.025em] leading-[1.05] text-slate-900 mt-4">
            Estimated CIF™ sits exactly where it should — right before the PO.
          </h2>
          <p className="text-slate-500 mt-5 text-[15px] leading-relaxed">
            It plugs natively into the DeMaxtore Workspace. Data flows in from RFQ™ and CommodityBid™, and out to Purchase Order, FreightIQ™ and beyond.
          </p>
        </div>

        {/* Desktop horizontal flow */}
        <div className="mt-16 hidden lg:block">
          <div className="relative">
            <div className="absolute top-[44px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
            <div className="grid grid-cols-8 gap-3 relative">
              {NODES.map((n, i) => (
                <div key={n.key} data-testid={CIF.workspaceNode(n.key)} className="flex flex-col items-center">
                  <div
                    className={[
                      "relative h-[88px] w-[88px] rounded-2xl border flex items-center justify-center transition-all",
                      n.current
                        ? "bg-blue-600 border-blue-600 text-white shadow-[0_0_0_8px_rgba(37,99,235,0.12),0_20px_40px_-15px_rgba(37,99,235,0.5)]"
                        : "bg-white border-slate-200 text-slate-700 hover:-translate-y-0.5 hover:shadow-md",
                    ].join(" ")}
                  >
                    <n.icon className="h-6 w-6" />
                    {n.current && (
                      <span className="absolute -top-2 -right-2 text-[9px] tracking-widest uppercase font-bold bg-white text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full shadow-sm">
                        You&rsquo;re here
                      </span>
                    )}
                  </div>
                  <div className="mono text-[10px] tracking-widest uppercase text-slate-400 font-semibold mt-4">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className={[
                    "font-display text-[13px] font-medium tracking-tight mt-1 text-center",
                    n.current ? "text-blue-700" : "text-slate-900",
                  ].join(" ")}>
                    {n.label}
                  </div>
                  <div className="text-[11px] text-slate-400 text-center mt-0.5 leading-tight">{n.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* approval band */}
          <div className="mt-14 rounded-2xl border border-blue-200 bg-blue-50/40 p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                <Calculator className="h-4 w-4" />
              </div>
              <div>
                <div className="font-display text-[15px] font-medium text-slate-900">
                  Estimated CIF™ approval gate
                </div>
                <div className="text-[12px] text-slate-500">
                  Workspace blocks Purchase Order issuance until landed cost is reviewed and approved.
                </div>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 pulse-dot" /> Active
            </div>
          </div>
        </div>

        {/* Mobile vertical flow */}
        <div className="mt-12 lg:hidden">
          <div className="relative pl-10">
            <div className="absolute left-[18px] top-2 bottom-2 w-px connector-line" />
            <div className="space-y-5">
              {NODES.map((n, i) => (
                <div key={n.key} data-testid={`${CIF.workspaceNode(n.key)}-mobile`} className="relative">
                  <div className={[
                    "absolute -left-10 top-3 h-9 w-9 rounded-lg flex items-center justify-center",
                    n.current ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-700",
                  ].join(" ")}>
                    <n.icon className="h-4 w-4" />
                  </div>
                  <div className={[
                    "rounded-xl border p-4",
                    n.current ? "border-blue-200 bg-blue-50/50" : "border-slate-200 bg-white",
                  ].join(" ")}>
                    <div className="flex items-center justify-between">
                      <div className="font-display text-[15px] font-medium text-slate-900">{n.label}</div>
                      <span className="mono text-[10px] tracking-widest uppercase text-slate-400 font-semibold">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="text-[12px] text-slate-500 mt-0.5">{n.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkspaceIntegration;
